//------------React/Redux
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import React from 'react';
import { bindActionCreators } from 'redux';
import { v4 as uuidv4 } from 'uuid';

//-------------Helpers
import * as validationHelper from '../../helpers/validationHelper';
import * as templateHelper from '../template/templateHelper';
import update from '../../helpers/update';
import get from 'lodash.get';

//-------------Components
import TicketRenderComponent from './components/ticketRenderComponent';
import { Message } from 'semantic-ui-react';
import { formBuilderActions } from '../../components/formbuilder/store';
import { cloneSection } from '../../components/formbuilder/formMappers';

//-------------Constants
import { i18Keys } from './translation';
import { TicketTypes } from '../../constants/ticketTypes';
import { paths } from './template';

//-------------Other
import './ticketing.css';

export class TicketBuilder extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      draggedIndex: null,
      dragOverIndex: null,
      reorderMode: false
    };
    this.draggedItem = null;
  }

  // Native HTML5 Drag and Drop handlers
  handleDragStart = (e, index) => {
    this.setState({ draggedIndex: index });
    this.draggedItem = this.props.value[index];
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (this.state.dragOverIndex !== index) {
      this.setState({ dragOverIndex: index });
    }
  };

  handleDragEnter = (e, index) => {
    e.preventDefault();
    this.setState({ dragOverIndex: index });
  };

  handleDragLeave = () => {
    this.setState({ dragOverIndex: null });
  };

  handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (this.state.draggedIndex === null || this.draggedItem === null) return;

    if (this.state.draggedIndex !== dropIndex) {
      const valueCopy = this.getDeepCopy(this.props.value);
      const draggedTicket = valueCopy.splice(this.state.draggedIndex, 1)[0];
      valueCopy.splice(dropIndex, 0, draggedTicket);

      this.props.onChange(valueCopy);
    }

    this.setState({ draggedIndex: null, dragOverIndex: null });
    this.draggedItem = null;
  };

  handleDragEnd = () => {
    this.setState({ draggedIndex: null, dragOverIndex: null });
    this.draggedItem = null;
  };

  getDeepCopy = (value) => {
    return JSON.parse(JSON.stringify(value));
  };
  
  onItemChange = (index, fieldKey, value) => {
    const newTickets = this.getNewTickets(index, fieldKey, value);
    this.props.onChange(newTickets);
  };

  onPriceChange = (index, fieldKey, value) => {
    let valueCopy = this.getDeepCopy(this.props.value);

    valueCopy = update.set(valueCopy, `${index}.${fieldKey}`, value);

    if (!value) {
      valueCopy = update.set(valueCopy, `${index}.${paths.tax}`, false);
    }

    this.props.onChange(valueCopy);
  };

  onFreeTicketChange = (index, fieldKey, value) => {
    let valueCopy = this.getDeepCopy(this.props.value);

    valueCopy = update.set(valueCopy, `${index}.${fieldKey}`, value ? null : 0);

    if (value) {
      valueCopy = update.set(valueCopy, `${index}.${paths.tax}`, false);
    }

    this.props.onChange(valueCopy);
  };

  getNewTickets(index, fieldKey, value) {
    let valueCopy = this.getDeepCopy(this.props.value);

    valueCopy = update.set(valueCopy, `${index}.${fieldKey}`, value);

    return valueCopy;
  }

  isUnlimited = (item) => {
    const quantity = get(item, paths.quantity);
    return quantity === null;
  };

  isFree = (item) => {
    const price = get(item, 'pricing.price');
    return price === null;
  };

  getTicketItems = () => {
    const tickets = this.props.value;
    const path = this.props.path;

    if (!tickets) {
      return null;
    }

    return tickets.map((item, index) => {
      const identifier = get(item, 'template.value.identifier');
      let isExpanded = get(item, 'template.value.isExpanded');
      // get isExpanded, default to true if null/undefined
      if (isExpanded === null || isExpanded === undefined) {
        isExpanded = true;
      }
      return (
        <div
          key={`${identifier}-${index}`}
          className={`ticket-wrapper ${this.state.draggedIndex === index ? 'dragging' : ''} ${this.state.dragOverIndex === index ? 'drag-over' : ''}`}
          onDragOver={(e) => this.handleDragOver(e, index)}
          onDragEnter={(e) => this.handleDragEnter(e, index)}
          onDragLeave={this.handleDragLeave}
          onDrop={(e) => this.handleDrop(e, index)}
        >
          <TicketRenderComponent
            formBuilderKey={`Ticket-${identifier}`}
            index={index}
            path={path}
            isFree={this.isFree(item)}
            isUnlimited={this.isUnlimited(item)}
            {...item}
            onDeleteClick={this.onDeleteTicketClick}
            onChange={this.onItemChange}
            onPriceChange={this.onPriceChange}
            onFreeTicketChange={this.onFreeTicketChange}
            isQuestionsEnabled={this.props.isQuestionsEnabled}
            country={this.props.country}
            currency={this.props.currency}
            purchaseFundraisingStyle={this.props.purchaseFundraisingStyle}
            isSystemAdmin={this.props.isSystemAdmin}
            isExpanded={this.state.reorderMode ? false : isExpanded}
            onCloneTicketClick={this.onAddTicketClick}
            onHandleDragStart={(e) => this.handleDragStart(e, index)}
            onHandleDragEnd={this.handleDragEnd}
            isDragging={this.state.draggedIndex !== null}
            showDragHandle={this.state.reorderMode}
            isReorderMode={this.state.reorderMode}
          />
        </div>
      );
    });
  };

  appendCloneName = (newTicket, ticketList) => {
    const newName = ' - Clone';
    const items = ticketList.filter((x) => newTicket.name === x.name);
    if (items.length > 0) {
      newTicket.name += newName;
      this.appendCloneName(newTicket, ticketList);
    }
  };

  onAddTicketClick = (cloneIndex) => {
    const valueCopy = this.props.value
      ? this.getDeepCopy(this.props.value)
      : [];

    // if cloneIndex present, clone that value at that index
    if (cloneIndex >= 0 && valueCopy.length > cloneIndex) {
      const newTicket = this.getDeepCopy(this.props.value[cloneIndex]);
      newTicket.id = undefined;
      this.appendCloneName(newTicket, valueCopy);
      newTicket.template.value.isExpanded = false;
      // link up formBuilder
      const id = uuidv4();
      newTicket.template.value.identifier = id;
      const formBuilder = newTicket.template.value.formBuilder;
      if (formBuilder) {
        cloneSection(formBuilder, newTicket?.fields?.fields, `Ticket-${id}`);
        this.props.formBuilderActions.setFormBuilderModel(formBuilder);
      }
      valueCopy.splice(cloneIndex + 1, 0, newTicket);
    }
    // else create new ticket
    else {
      valueCopy.push({
        name: '',
        pricing: {
          price: 0,
          hasCountryTax: false
        },
        inventory: {
          quantity: 0
        },
        description: '',
        type: TicketTypes.Participant,
        stream: '',
        template: {
          value: {
            identifier: uuidv4()
          }
        },
        availableUntil: '',
        availableFrom: ''
      });
    }
    this.props.onChange(valueCopy);
  };

  onDeleteTicketClick = (index) => {
    const valueCopy = this.getDeepCopy(this.props.value);
    valueCopy.splice(index, 1);
    this.props.onChange(valueCopy);
  };

  render() {
    const ticketItems = this.getTicketItems();
    const i18Key =
      'template.page.content.ticketing.tickets.available-tickets.ticket';

    return (
      <div className="tickets-container">
        <div className="tickets-toolbar">
          <span
            className={`tickets-toolbar-link ${this.state.reorderMode ? 'active' : ''}`}
            onClick={() => this.setState({ reorderMode: !this.state.reorderMode })}
          >
            {this.state.reorderMode
              ? I18n.t(`${i18Key}.reorder.exit`)
              : I18n.t(`${i18Key}.reorder.enter`)}
          </span>
        </div>
        {ticketItems}
        <div className="add-ticket-container" onClick={this.onAddTicketClick}>
          <span className="add-ticket-text">
            {I18n.t(`${i18Key}.${i18Keys.ADD_NEW_TICKET}`)}
          </span>
        </div>
        <Message negative hidden={this.props.isValid}>
          <p>{this.props.errorMessage}</p>
        </Message>
      </div>
    );
  }
}

const mapState = (state, ownProps) => {
  const value = templateHelper.getValue(state.templateBuilderEx, ownProps.path);

  return {
    value: value,
    isValid: validationHelper.isValid(ownProps.validation, value),
    errorMessage: validationHelper.getValidationErrorMessage(
      ownProps.validation,
      value
    ),
    country: get(state, 'templateBuilderEx.data.organization.country'),
    currency: get(state, 'templateBuilderEx.data.organization.currency'),
    purchaseFundraisingStyle: get(
      state,
      'templateBuilderEx.data.ticketing.purchaseFundraisingStyle'
    ),
    isSystemAdmin: get(state, 'session.isSystemAdmin')
  };
};

/** Maps the actions to properties */
const mapDispatch = (dispatch) => {
  return {
    formBuilderActions: bindActionCreators(formBuilderActions, dispatch)
  };
};

const withoutContext = connect(mapState, mapDispatch);
export default withoutContext(TicketBuilder);
export { withoutContext };
